export function parseJobDescription(job) {
  try {
    return JSON.parse(job.description);
  } catch {
    return {};
  }
}

/** Stored name on disk, e.g. `{uuid}_train.log` (see worker add_files). */
export function getTrainLogFilenameForModel(model) {
  if (!model || model.meta == null || model.meta === "") {
    return null;
  }
  let meta;
  try {
    meta =
      typeof model.meta === "string"
        ? JSON.parse(model.meta || "{}")
        : model.meta;
  } catch {
    return null;
  }
  if (!meta || !Array.isArray(meta.files)) {
    return null;
  }
  for (const f of meta.files) {
    const base = String(f).split("/").pop();
    if (base && base.endsWith("train.log")) {
      return base;
    }
  }
  return null;
}

export function modelHasTrainLogFile(model) {
  return getTrainLogFilenameForModel(model) != null;
}

export function jobHasTrainMetrics(job) {
  const desc = parseJobDescription(job);
  const m = desc.train_metrics;
  if (!m || typeof m !== 'object') {
    return false;
  }
  const hasLossSeries = ['iou_losses', 'conf_losses', 'cls_losses', 'l1_losses'].some(
    (key) => m[key] && m[key].length > 0,
  );
  return (
    m.current_epoch != null ||
    (m.train_losses && m.train_losses.length > 0) ||
    (m.val_losses && m.val_losses.length > 0) ||
    (m.accuracy && m.accuracy.length > 0) ||
    (m.map50_95_series && m.map50_95_series.length > 0) ||
    (m.map50_series && m.map50_series.length > 0) ||
    hasLossSeries
  );
}

/** Train job marked Failed via worker update_job_state. */
export function isFailedTrainJob(job) {
  const desc = parseJobDescription(job);
  return String(desc.state || '').toLowerCase() === 'failed';
}

export function jobHasErrorLogs(job) {
  return !!(job && job.logs && String(job.logs).trim());
}

/** Metrics chart and/or error traceback available for the train-log modal. */
export function jobCanShowTrainLog(job) {
  return jobHasTrainMetrics(job) || jobHasErrorLogs(job);
}

/**
 * True when description.state is the training loop (or unset), so epoch metrics
 * may safely drive the chip label. Prep / eval / export states must win over
 * leftover train_metrics after training finishes.
 */
export function isTrainingPhaseState(state) {
  if (state == null || state === '') {
    return true;
  }
  const s = String(state).toLowerCase().trim();
  if (s === 'failed') {
    return false;
  }
  if (
    s.startsWith('evaluating') ||
    s.startsWith('exporting') ||
    s.startsWith('adding model') ||
    s.startsWith('computing') ||
    s.startsWith('uploading') ||
    s.includes('analysing') ||
    s.includes('analyzing') ||
    s.startsWith('loading') ||
    s.startsWith('downloading') ||
    s.startsWith('generating') ||
    s.startsWith('generate ') ||
    s.startsWith('extracting') ||
    s.startsWith('creating') ||
    s.startsWith('applying') ||
    s.startsWith('preparing')
  ) {
    return false;
  }
  return (
    s === 'training' ||
    s.startsWith('training') ||
    s.startsWith('epoch ') ||
    /^epoch\s*\d/.test(s)
  );
}

const FRIENDLY_STATE_LABELS = {
  'evaluating model': 'Evaluating · preparing validation',
  'exporting models': 'Exporting models',
  'adding model to box21': 'Saving model',
  'loading annotations': 'Preparing · loading annotations',
  'loading empty assets': 'Preparing · loading empty assets',
  'downloading assets': 'Preparing · downloading images',
  'generating crops': 'Preparing · generating crops',
  'generate train config files': 'Preparing · train config',
  training: 'Training',
};

/**
 * Map raw worker states to short user-facing labels. Rich states that already
 * include counts / ETA (e.g. "Evaluating · analysing images: 12/40 · ~2m")
 * are returned as-is.
 */
export function friendlyJobStateLabel(state) {
  if (!state) {
    return '';
  }
  const raw = String(state).trim();
  const key = raw.toLowerCase();
  if (FRIENDLY_STATE_LABELS[key]) {
    return FRIENDLY_STATE_LABELS[key];
  }
  // "Downloading assets: 12/40" → friendlier prefix
  const downloadMatch = raw.match(/^Downloading assets:\s*(.+)$/i);
  if (downloadMatch) {
    return `Preparing · downloading images: ${downloadMatch[1]}`;
  }
  return raw;
}

function formatEvalMetricsLabel(evalMetrics) {
  if (!evalMetrics || typeof evalMetrics !== 'object') {
    return null;
  }
  const step = String(evalMetrics.step || '').toLowerCase();
  const stepLabels = {
    downloading_images: 'Evaluating · downloading images',
    analysing_images: 'Evaluating · analysing images',
    analyzing_images: 'Evaluating · analysing images',
    computing_performance: 'Evaluating · computing performance',
    preparing: 'Evaluating · preparing validation',
  };
  let label = stepLabels[step] || (evalMetrics.label ? String(evalMetrics.label) : null);
  if (!label) {
    return null;
  }
  const current = evalMetrics.current;
  const total = evalMetrics.total;
  if (current != null && total != null && Number(total) > 0) {
    label = `${label}: ${current}/${total}`;
  }
  if (evalMetrics.eta_display) {
    label = `${label} · ${evalMetrics.eta_display}`;
  }
  return label;
}

export function trainingStateLabel(description, metrics) {
  const desc = description || {};
  const state = desc.state;
  const evalLabel = formatEvalMetricsLabel(desc.eval_metrics);
  if (evalLabel) {
    return evalLabel;
  }
  if (state && !isTrainingPhaseState(state)) {
    return friendlyJobStateLabel(state);
  }
  if (metrics && metrics.current_epoch != null && metrics.total_epochs) {
    const eta = metrics.eta_display ? ` · ${metrics.eta_display}` : '';
    let label = `Training · epoch ${metrics.current_epoch}/${metrics.total_epochs}${eta}`;
    const extras = [];
    if (metrics.train_loss != null) {
      extras.push(`loss ${Number(metrics.train_loss).toFixed(3)}`);
    }
    if (metrics.accuracy_latest != null) {
      extras.push(`acc ${(Number(metrics.accuracy_latest) * 100).toFixed(1)}%`);
    } else if (metrics.accuracy && metrics.accuracy.length) {
      const acc = metrics.accuracy[metrics.accuracy.length - 1];
      extras.push(`acc ${(Number(acc) * 100).toFixed(1)}%`);
    }
    if (metrics.map50 != null) {
      extras.push(`mAP@.5 ${Number(metrics.map50).toFixed(3)}`);
    } else if (metrics.map50_95 != null) {
      extras.push(`mAP ${Number(metrics.map50_95).toFixed(3)}`);
    }
    const comp = metrics.loss_components;
    if (comp && typeof comp === 'object' && metrics.train_loss == null) {
      const parts = Object.keys(comp)
        .sort()
        .map((k) => `${k}:${Number(comp[k]).toFixed(2)}`);
      if (parts.length) {
        extras.push(parts.join(' '));
      }
    }
    if (extras.length) {
      label = `${label} · ${extras.join(' · ')}`;
    }
    return label;
  }
  if (state) {
    return friendlyJobStateLabel(state);
  }
  return 'In queue';
}

/** Prefer eval_metrics progress, else job.progress (0–100). */
export function jobDisplayProgress(job) {
  if (!job || job.processed === 1) {
    return 0;
  }
  const desc = parseJobDescription(job);
  const em = desc.eval_metrics;
  if (em && em.total != null && Number(em.total) > 0 && em.current != null) {
    return Math.min(100, Math.round((Number(em.current) / Number(em.total)) * 100));
  }
  return Number(job.progress) || 0;
}
